import React, { useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { gsap } from 'gsap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DataFilters } from '../atoms'
import { colors } from '../../consts'
import DateRangeInput from '../DateRange'
import { SEARCH_MODES } from '../../hooks/useCallSearchData'
import styles from './CallSearchFilter.module.scss'
import useAuthz from '../../hooks/useAuthz'

const CallSearchFilter = ({
  searchMode,
  campaignName,
  callId,
  startDate,
  endDate,
  formErrors,
  campaigns,
  loadingCampaigns,
  loading,

  onSearchModeChange,
  onCampaignNameChange,
  onCallIdChange,
  onStartDateChange,
  onEndDateChange,
  onSearch,

  disposition,
  direction,
  agentName,
  durationMin,
  durationMax,
  onDispositionChange,
  onDirectionChange,
  onAgentNameChange,
  onDurationMinChange,
  onDurationMaxChange,
  filterOptions
}) => {
  const { isAdmin } = useAuthz()
  const { t } = useTranslation()
  const [showAdvanced, setShowAdvanced] = useState(false)

  const campaignRef = useRef(null)
  const dateRangeRef = useRef(null)
  const callIdRef = useRef(null)

  useEffect(() => {
    const elements = [campaignRef.current, dateRangeRef.current].filter(Boolean)
    if (elements.length > 0 && gsap) {
      gsap.fromTo(
        elements,
        { y: 15, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.5,
          stagger: 0.15,
          ease: 'power2.out'
        }
      )
    }
  }, [searchMode])

  useEffect(() => {
    if (callIdRef.current && gsap) {
      gsap.fromTo(
        callIdRef.current,
        { y: 15, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' }
      )
    }
  }, [searchMode])

  const handleStartDateChange = (e) => {
    onStartDateChange(e.target.value)
  }

  const handleEndDateChange = (e) => {
    onEndDateChange(e.target.value)
  }

  return (
    <div className={styles.filterSection}>
      {/* Compact inline bar: tabs + fields + search */}
      <div className={styles.filterBar}>
        <div className={styles.tabsContainer}>
          <Tabs
            value={searchMode === SEARCH_MODES.DATE_ONLY ? SEARCH_MODES.CAMPAIGN_AND_DATE : searchMode}
            onChange={onSearchModeChange}
            className={styles.tabs}
            classes={{ indicator: styles.tabIndicator }}
          >
            <Tab
              className={styles.tab}
              label={
                <Box display='flex' alignItems='center'>
                  <FontAwesomeIcon icon='calendar-alt' className={styles.tabIcon} />
                  {t('calls.searchModes.dateRange')}
                </Box>
              }
            />
            <Tab
              className={styles.tab}
              label={
                <Box display='flex' alignItems='center'>
                  <FontAwesomeIcon icon='hashtag' className={styles.tabIcon} />
                  {t('calls.searchModes.callId')}
                </Box>
              }
            />
          </Tabs>
        </div>

        {/* Inline filter fields */}
        <div className={styles.fieldsRow}>
          {(searchMode === SEARCH_MODES.CAMPAIGN_AND_DATE || searchMode === SEARCH_MODES.DATE_ONLY) && (
            <>
              {isAdmin && (
                <div ref={campaignRef} className={styles.campaignField} style={{ opacity: 0 }}>
                  <FormControl
                    variant='outlined'
                    className={styles.formControl}
                    fullWidth
                    size='small'
                  >
                    <InputLabel id='campaign-label'>
                      {t('calls.campaign')} (Optional)
                    </InputLabel>
                    <Select
                      labelId='campaign-label'
                      value={campaignName || ''}
                      onChange={(e) => onCampaignNameChange(e.target.value || '')}
                      label={`${t('calls.campaign')} (Optional)`}
                      disabled={loadingCampaigns}
                    >
                      <MenuItem value=''>
                        <em>All Campaigns</em>
                      </MenuItem>
                      {campaigns.map((campaign) => (
                        <MenuItem key={campaign.original_name} value={campaign.campaign_name}>
                          {campaign.campaign_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
              <div ref={dateRangeRef} className={styles.dateField} style={{ opacity: 0 }}>
                <DateRangeInput
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={handleStartDateChange}
                  onEndDateChange={handleEndDateChange}
                />
              </div>
            </>
          )}

          {searchMode === SEARCH_MODES.CALL_ID && (
            <div ref={callIdRef} className={styles.callIdField} style={{ opacity: 0 }}>
              <TextField
                fullWidth
                label={t('calls.callId')}
                variant='outlined'
                size='small'
                value={callId || ''}
                onChange={(e) => onCallIdChange(e.target.value || '')}
                required
                error={formErrors.callId}
                helperText={formErrors.callId ? t('errors.required', { field: t('calls.callId') }) : ''}
                className={styles.textField}
              />
            </div>
          )}

          <Button
            variant='contained'
            color='primary'
            onClick={() => onSearch(t)}
            disabled={loading}
            className={styles.searchButton}
            startIcon={<FontAwesomeIcon icon='search' />}
          >
            {loading ? <CircularProgress size={20} color='inherit' /> : 'Search'}
          </Button>
        </div>
      </div>

      {/* Advanced Filters Toggle */}
      {searchMode !== SEARCH_MODES.CALL_ID && filterOptions && (
        <>
          <button
            type='button'
            className={styles.advancedToggle}
            onClick={() => setShowAdvanced(!showAdvanced)}
            aria-expanded={showAdvanced}
          >
            <FontAwesomeIcon
              icon={showAdvanced ? 'chevron-up' : 'chevron-down'}
              className={styles.advancedIcon}
            />
            Advanced Filters
          </button>

          {showAdvanced && (
            <DataFilters className={styles.advancedFilters}>
              {/* Disposition */}
              <div className={styles.filterField}>
                <Autocomplete
                  multiple
                  size='small'
                  options={filterOptions.dispositions || []}
                  value={disposition || []}
                  onChange={(_, newValue) => onDispositionChange(newValue)}
                  className={styles.autocomplete}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='outlined'
                      label='Disposition'
                      placeholder={disposition.length === 0 ? 'All' : ''}
                      size='small'
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option}
                        label={option}
                        size='small'
                        variant='outlined'
                        {...getTagProps({ index })}
                        style={{ borderColor: colors.ui.deepBlue, color: colors.ui.deepBlue }}
                      />
                    ))
                  }
                />
              </div>

              {/* Direction */}
              <div className={styles.filterField}>
                <Autocomplete
                  multiple
                  size='small'
                  options={filterOptions.directions || []}
                  value={direction || []}
                  onChange={(_, newValue) => onDirectionChange(newValue)}
                  className={styles.autocomplete}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='outlined'
                      label='Direction'
                      placeholder={direction.length === 0 ? 'All' : ''}
                      size='small'
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option}
                        label={option}
                        size='small'
                        variant='outlined'
                        {...getTagProps({ index })}
                        style={{ borderColor: colors.ui.deepBlue, color: colors.ui.deepBlue }}
                      />
                    ))
                  }
                />
              </div>

              {/* Agent */}
              <div className={styles.filterField}>
                <Autocomplete
                  multiple
                  size='small'
                  options={filterOptions.agents || []}
                  value={agentName || []}
                  onChange={(_, newValue) => onAgentNameChange(newValue)}
                  className={styles.autocomplete}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='outlined'
                      label='Agent'
                      placeholder={agentName.length === 0 ? 'All' : ''}
                      size='small'
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option}
                        label={option}
                        size='small'
                        variant='outlined'
                        {...getTagProps({ index })}
                        style={{ borderColor: colors.ui.deepBlue, color: colors.ui.deepBlue }}
                      />
                    ))
                  }
                />
              </div>

              {/* Duration Range */}
              <div className={styles.durationGroup}>
                <TextField
                  label='Min (sec)'
                  variant='outlined'
                  size='small'
                  type='number'
                  value={durationMin || ''}
                  onChange={(e) => onDurationMinChange(e.target.value || '')}
                  className={`${styles.textField} ${styles.durationField}`}
                  inputProps={{ min: 0 }}
                />
                <span className={styles.durationSeparator}>to</span>
                <TextField
                  label='Max (sec)'
                  variant='outlined'
                  size='small'
                  type='number'
                  value={durationMax || ''}
                  onChange={(e) => onDurationMaxChange(e.target.value || '')}
                  className={`${styles.textField} ${styles.durationField}`}
                  inputProps={{ min: 0 }}
                />
              </div>
            </DataFilters>
          )}
        </>
      )}
    </div>
  )
}

CallSearchFilter.propTypes = {
  searchMode: PropTypes.number.isRequired,
  campaignName: PropTypes.string,
  callId: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  formErrors: PropTypes.shape({
    campaignName: PropTypes.bool,
    callId: PropTypes.bool
  }).isRequired,
  campaigns: PropTypes.arrayOf(PropTypes.shape({
    campaign_name: PropTypes.string,
    original_name: PropTypes.string
  })).isRequired,
  loadingCampaigns: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onSearchModeChange: PropTypes.func.isRequired,
  onCampaignNameChange: PropTypes.func.isRequired,
  onCallIdChange: PropTypes.func.isRequired,
  onStartDateChange: PropTypes.func.isRequired,
  onEndDateChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  disposition: PropTypes.string,
  direction: PropTypes.string,
  agentName: PropTypes.string,
  durationMin: PropTypes.string,
  durationMax: PropTypes.string,
  onDispositionChange: PropTypes.func.isRequired,
  onDirectionChange: PropTypes.func.isRequired,
  onAgentNameChange: PropTypes.func.isRequired,
  onDurationMinChange: PropTypes.func.isRequired,
  onDurationMaxChange: PropTypes.func.isRequired,
  filterOptions: PropTypes.shape({
    dispositions: PropTypes.arrayOf(PropTypes.string),
    directions: PropTypes.arrayOf(PropTypes.string),
    agents: PropTypes.arrayOf(PropTypes.string)
  })
}

export default CallSearchFilter
